import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";

import { CopyButton } from "@/components/copy-button";
import { Button } from "@/components/ui/button";
import { maskKey } from "@/lib/auth";
import { cn } from "@/lib/utils";

/**
 * The reader's own API key, masked until asked for, with a copy button.
 *
 * Showing it is not a new exposure: the browser already holds this key (the student
 * pasted it to sign in), and the server stores it in the clear so the Slack bot can send
 * it. What this adds is a way back to it without asking a TA -- for the second laptop, the
 * `export VIS_NAV_API_KEY=` line, the teammate's machine at the demo. Masked by default
 * because screens are shared more often than clipboards.
 */
export function ApiKeyRow({ apiKey, className }: { apiKey: string; className?: string }) {
  const [shown, setShown] = useState(false);
  return (
    <div className={cn("flex min-w-0 items-center gap-1", className)}>
      <code
        className={cn(
          "bg-muted min-w-0 flex-1 truncate rounded px-1.5 py-1 font-mono text-xs",
          !shown && "tracking-wider",
        )}
        title={shown ? apiKey : undefined}
        aria-label={shown ? "Your API key" : "Your API key, hidden"}
      >
        {shown ? apiKey : maskKey(apiKey)}
      </code>
      <Button
        type="button"
        size="icon-xs"
        variant="ghost"
        onClick={() => setShown((v) => !v)}
        aria-label={shown ? "Hide key" : "Show key"}
        aria-pressed={shown}
      >
        {shown ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
      </Button>
      <CopyButton text={apiKey} label="Copy key" copiedLabel="Copied" iconOnly size="icon-xs" variant="ghost" />
    </div>
  );
}
